import React from 'react';
import * as THREE from 'three';
import type { NPCModelProps, NPCModelRegistry } from '../types';

const Rabbit = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.1, 0]} castShadow>
        <sphereGeometry args={[0.1, 10, 10]} />
        <meshStandardMaterial
          color={world === 'real' ? '#FFFAFA' : '#E8E8F0'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.2, 0.08]} castShadow>
        <sphereGeometry args={[0.06, 10, 10]} />
        <meshStandardMaterial color={world === 'real' ? '#FFFAFA' : '#E8E8F0'} />
      </mesh>
      {/* Long ears */}
      {[-0.03, 0.03].map((x, i) => (
        <mesh key={i} position={[x, 0.35, 0.05]} rotation={[0.2, 0, (i - 0.5) * 0.2]}>
          <capsuleGeometry args={[0.015, 0.1, 4, 6]} />
          <meshStandardMaterial color={world === 'real' ? '#FFFAFA' : '#E8E8F0'} />
        </mesh>
      ))}
      {/* Inner ears */}
      {[-0.03, 0.03].map((x, i) => (
        <mesh key={i} position={[x, 0.34, 0.06]} rotation={[0.2, 0, (i - 0.5) * 0.2]}>
          <capsuleGeometry args={[0.008, 0.08, 4, 6]} />
          <meshStandardMaterial color={world === 'real' ? '#FFB6C1' : '#c0a0a0'} />
        </mesh>
      ))}
      {/* Snout */}
      <mesh position={[0, 0.18, 0.13]}>
        <sphereGeometry args={[0.025, 8, 8]} />
        <meshStandardMaterial color={world === 'real' ? '#FFB6C1' : '#c0a0a0'} />
      </mesh>
      {/* Nose */}
      <mesh position={[0, 0.19, 0.15]}>
        <sphereGeometry args={[0.01, 6, 6]} />
        <meshStandardMaterial color="#FFB6C1" />
      </mesh>
      {/* Fluffy tail */}
      <mesh position={[0, 0.1, -0.1]}>
        <sphereGeometry args={[0.04, 8, 8]} />
        <meshStandardMaterial color={world === 'real' ? '#FFFAFA' : '#E8E8F0'} />
      </mesh>
      {/* Eyes */}
      {[-0.025, 0.025].map((x, i) => (
        <mesh key={i} position={[x, 0.22, 0.12]}>
          <sphereGeometry args={[0.012, 8, 8]} />
          <meshBasicMaterial color="#8B0000" />
        </mesh>
      ))}
    </group>
  );
};

const Frog = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.08, 0]} castShadow>
        <sphereGeometry args={[0.1, 10, 10]} />
        <meshStandardMaterial
          color={world === 'real' ? '#228B22' : '#1a5a2a'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.12, 0.08]} castShadow>
        <sphereGeometry args={[0.06, 10, 10]} />
        <meshStandardMaterial color={world === 'real' ? '#228B22' : '#1a5a2a'} />
      </mesh>
      {/* Big eyes on top */}
      {[-0.04, 0.04].map((x, i) => (
        <group key={i} position={[x, 0.18, 0.06]}>
          <mesh>
            <sphereGeometry args={[0.03, 8, 8]} />
            <meshStandardMaterial color={world === 'real' ? '#228B22' : '#1a5a2a'} />
          </mesh>
          <mesh position={[0, 0.015, 0.015]}>
            <sphereGeometry args={[0.018, 8, 8]} />
            <meshBasicMaterial color="#FFD700" />
          </mesh>
          <mesh position={[0, 0.02, 0.02]}>
            <sphereGeometry args={[0.008, 6, 6]} />
            <meshBasicMaterial color="#000000" />
          </mesh>
        </group>
      ))}
      {/* Front legs */}
      {[-0.08, 0.08].map((x, i) => (
        <mesh key={i} position={[x, 0.03, 0.08]} rotation={[0, 0, x > 0 ? 0.5 : -0.5]}>
          <capsuleGeometry args={[0.015, 0.06, 4, 6]} />
          <meshStandardMaterial color={world === 'real' ? '#228B22' : '#1a5a2a'} />
        </mesh>
      ))}
      {/* Back legs - bent */}
      {[-0.06, 0.06].map((x, i) => (
        <group key={i} position={[x, 0.05, -0.08]}>
          <mesh rotation={[0.5, 0, x > 0 ? 0.3 : -0.3]}>
            <capsuleGeometry args={[0.02, 0.08, 4, 6]} />
            <meshStandardMaterial color={world === 'real' ? '#228B22' : '#1a5a2a'} />
          </mesh>
        </group>
      ))}
    </group>
  );
};

const Heron = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.4, 0]} rotation={[-0.2, 0, 0]} castShadow>
        <capsuleGeometry args={[0.08, 0.15, 8, 16]} />
        <meshStandardMaterial
          color={world === 'real' ? '#708090' : '#506070'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Neck - long and curved */}
      <mesh position={[0, 0.55, 0.08]} rotation={[-0.3, 0, 0]} castShadow>
        <cylinderGeometry args={[0.02, 0.03, 0.2, 8]} />
        <meshStandardMaterial color={world === 'real' ? '#FFFAFA' : '#E8E8F0'} />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.65, 0.12]} castShadow>
        <sphereGeometry args={[0.04, 10, 10]} />
        <meshStandardMaterial color={world === 'real' ? '#FFFAFA' : '#E8E8F0'} />
      </mesh>
      {/* Long beak */}
      <mesh position={[0, 0.63, 0.2]} rotation={[0.2, 0, 0]}>
        <coneGeometry args={[0.012, 0.12, 4]} />
        <meshStandardMaterial color={world === 'real' ? '#FFD700' : '#b0a030'} />
      </mesh>
      {/* Long legs */}
      {[-0.03, 0.03].map((x, i) => (
        <mesh key={i} position={[x, 0.15, 0]}>
          <cylinderGeometry args={[0.01, 0.012, 0.3, 6]} />
          <meshStandardMaterial color={world === 'real' ? '#2F2F2F' : '#3a3a3a'} />
        </mesh>
      ))}
      {/* Wings folded */}
      {[-0.08, 0.08].map((x, i) => (
        <mesh key={i} position={[x, 0.4, -0.02]} rotation={[0, 0, x > 0 ? 0.2 : -0.2]}>
          <planeGeometry args={[0.1, 0.18]} />
          <meshStandardMaterial
            color={world === 'real' ? '#708090' : '#506070'}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}
      {/* Eye */}
      <mesh position={[0.025, 0.66, 0.14]}>
        <sphereGeometry args={[0.008, 6, 6]} />
        <meshBasicMaterial color="#FFD700" />
      </mesh>
    </group>
  );
};

const Crocodile = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.08, 0]} castShadow>
        <boxGeometry args={[0.15, 0.1, 0.4]} />
        <meshStandardMaterial
          color={world === 'real' ? '#556B2F' : '#3a4a2a'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Head/snout */}
      <mesh position={[0, 0.08, 0.28]} castShadow>
        <boxGeometry args={[0.1, 0.06, 0.2]} />
        <meshStandardMaterial color={world === 'real' ? '#556B2F' : '#3a4a2a'} />
      </mesh>
      {/* Upper jaw */}
      <mesh position={[0, 0.1, 0.35]}>
        <boxGeometry args={[0.08, 0.03, 0.12]} />
        <meshStandardMaterial color={world === 'real' ? '#4a5a2a' : '#3a4a2a'} />
      </mesh>
      {/* Teeth */}
      {[-0.03, 0, 0.03].map((x, i) => (
        <mesh key={i} position={[x, 0.07, 0.38]} rotation={[Math.PI, 0, 0]}>
          <coneGeometry args={[0.008, 0.02, 4]} />
          <meshStandardMaterial color="#FFFAFA" />
        </mesh>
      ))}
      {/* Eyes on top */}
      {[-0.035, 0.035].map((x, i) => (
        <mesh key={i} position={[x, 0.13, 0.25]}>
          <sphereGeometry args={[0.015, 8, 8]} />
          <meshBasicMaterial color="#FFD700" />
        </mesh>
      ))}
      {/* Tail */}
      <mesh position={[0, 0.06, -0.28]} rotation={[0.1, 0, 0]}>
        <coneGeometry args={[0.06, 0.25, 6]} />
        <meshStandardMaterial color={world === 'real' ? '#556B2F' : '#3a4a2a'} />
      </mesh>
      {/* Legs */}
      {[[-0.08, 0.1], [0.08, 0.1], [-0.08, -0.1], [0.08, -0.1]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.03, z]} rotation={[0, 0, x > 0 ? 0.8 : -0.8]}>
          <capsuleGeometry args={[0.02, 0.04, 4, 6]} />
          <meshStandardMaterial color={world === 'real' ? '#556B2F' : '#3a4a2a'} />
        </mesh>
      ))}
      {/* Scales on back */}
      {[0, 1, 2, 3, 4].map((i) => (
        <mesh key={i} position={[0, 0.14, 0.15 - i * 0.08]}>
          <coneGeometry args={[0.02, 0.03, 4]} />
          <meshStandardMaterial color={world === 'real' ? '#4a5a2a' : '#2a3a1a'} />
        </mesh>
      ))}
    </group>
  );
};

const Firefly = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.3, 0]} castShadow>
        <capsuleGeometry args={[0.02, 0.04, 4, 8]} />
        <meshStandardMaterial
          color={world === 'real' ? '#2F2F2F' : '#3a3a4a'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Glowing abdomen */}
      <mesh position={[0, 0.27, 0]}>
        <sphereGeometry args={[0.025, 8, 8]} />
        <meshStandardMaterial
          color={world === 'real' ? '#ADFF2F' : '#90EE90'}
          emissive={world === 'real' ? '#ADFF2F' : '#90EE90'}
          emissiveIntensity={1}
        />
      </mesh>
      {/* Wings */}
      {[-0.03, 0.03].map((x, i) => (
        <mesh key={i} position={[x, 0.32, 0]} rotation={[0.2, 0, x > 0 ? 0.4 : -0.4]}>
          <planeGeometry args={[0.04, 0.03]} />
          <meshStandardMaterial
            color="#B0C4DE"
            transparent
            opacity={0.5}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}
      {/* Glow light */}
      <pointLight
        position={[0, 0.27, 0]}
        color={world === 'real' ? '#ADFF2F' : '#90EE90'}
        intensity={0.5}
        distance={1.5}
      />
    </group>
  );
};

const Duck = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.12, 0]} castShadow>
        <capsuleGeometry args={[0.08, 0.1, 8, 16]} />
        <meshStandardMaterial
          color={world === 'real' ? '#8B4513' : '#5a3a2a'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.22, 0.1]} castShadow>
        <sphereGeometry args={[0.05, 10, 10]} />
        <meshStandardMaterial color={world === 'real' ? '#228B22' : '#1a5a2a'} />
      </mesh>
      {/* Beak */}
      <mesh position={[0, 0.2, 0.16]} rotation={[0.2, 0, 0]}>
        <boxGeometry args={[0.04, 0.015, 0.04]} />
        <meshStandardMaterial color={world === 'real' ? '#FFA500' : '#b07030'} />
      </mesh>
      {/* Wings */}
      {[-0.08, 0.08].map((x, i) => (
        <mesh key={i} position={[x, 0.12, 0]} rotation={[0, 0, x > 0 ? 0.3 : -0.3]}>
          <planeGeometry args={[0.08, 0.1]} />
          <meshStandardMaterial
            color={world === 'real' ? '#8B4513' : '#5a3a2a'}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}
      {/* Tail */}
      <mesh position={[0, 0.15, -0.1]} rotation={[-0.5, 0, 0]}>
        <coneGeometry args={[0.03, 0.05, 4]} />
        <meshStandardMaterial color={world === 'real' ? '#8B4513' : '#5a3a2a'} />
      </mesh>
      {/* Eyes */}
      {[-0.02, 0.02].map((x, i) => (
        <mesh key={i} position={[x, 0.24, 0.13]}>
          <sphereGeometry args={[0.008, 6, 6]} />
          <meshBasicMaterial color="#000000" />
        </mesh>
      ))}
    </group>
  );
};

export const swampAnimals: NPCModelRegistry = {
  rabbit: Rabbit,
  frog: Frog,
  heron: Heron,
  crocodile: Crocodile,
  firefly: Firefly,
  duck: Duck,
};
